import { useEffect, useMemo, useRef } from 'react'

import { useF0FormDefinition, f0FormField, useF0Form } from '@factorialco/f0-react'
import { UpsertAiCreditsCreditSettingDocument } from 'generated/resources/graphql'
import { z } from 'zod'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

const useCreditsSchema = () => {
  return useMemo(
    () =>
      z.object({
        employeeCreditCap: f0FormField(z.number().int().min(0).optional().nullable(), {
          label: i18n.t('ai.settings.credits_tab.form.monthly_employee_allowance_label'),
          helpText: i18n.t('ai.settings.credits_tab.form.monthly_employee_allowance_helper'),
          fieldType: 'money' as const,
          currency: 'credits',
        }),
      }),
    []
  )
}

interface UseCreditsFormParams {
  employeeCreditCap: number | null | undefined
}

export const useCreditsForm = ({ employeeCreditCap }: UseCreditsFormParams) => {
  const schema = useCreditsSchema()
  const { formRef, setValue } = useF0Form()
  const lastSavedCapRef = useRef<number | null>(employeeCreditCap ?? null)

  const [upsertCreditSetting] = useMutation(UpsertAiCreditsCreditSettingDocument, {
    extractorFn: (data) => data.aiCreditsMutations.upsertAicreditsCreditSetting,
  })

  // TODO: This is a workaround since F0Form doesn't update the value
  // when the input is emptied
  useEffect(() => {
    setValue('employeeCreditCap', employeeCreditCap ?? null)
  }, [employeeCreditCap, setValue])

  const formDefinition = useF0FormDefinition({
    name: 'ai-credits-per-employee',
    schema,
    submitConfig: {
      type: 'action-bar',
      label: i18n.t('common.save'),
    },
    onSubmit: async ({ data }) => {
      let errors: Record<string, string> = {}

      const result = await upsertCreditSetting({
        variables: {
          employeeCreditCap: data.employeeCreditCap ?? null,
        },
      })

      const handled = handleResult(result, (formErrors) => {
        errors = formErrors
      })

      if (handled.success) {
        const nextValue = data.employeeCreditCap ?? null
        if (nextValue !== lastSavedCapRef.current) {
          if (nextValue == null) {
            track(trackingEvents.one.creditsPerEmployeeRemove)
          } else {
            track(trackingEvents.one.creditsPerEmployeeEdit, { amount: nextValue })
          }
          lastSavedCapRef.current = nextValue
        }

        return { success: true }
      }

      return { success: false, errors }
    },
  })

  return { formRef, formDefinition }
}
